import {HierarchyNode} from '@/components/app/hierarchy';
import type {BiobookChapter} from '../types';
export class TableOfContents {
  public nodes: HierarchyNode[] = [];
  public flatNodes: HierarchyNode[] = [];
  constructor(
    private biobookChapters: BiobookChapter[],
    private noEmptyRoots = false,
  ) {
    this.nodes = this.buildNodes();
    this.flatNodes = this.nodes.flatMap((node) => this.flattenNode(node));
  }
  public getAscendantNodes(node: HierarchyNode): HierarchyNode[] {
    const ascendants: HierarchyNode[] = [];
    let currentNode: HierarchyNode | null = node;
    while (currentNode) {
      const parentNode = this.getParentNode(currentNode);
      if (parentNode) {
        ascendants.unshift(parentNode);
      }
      currentNode = parentNode;
    }
    return ascendants;
  }
  public getParentNode(node: HierarchyNode): HierarchyNode | null {
    return this.flatNodes.find((n) => n.children?.includes(node)) ?? null;
  }
  private flattenNode(node: HierarchyNode): HierarchyNode[] {
    return [node, ...(node.children?.flatMap((child) => this.flattenNode(child)) ?? [])];
  }
  private buildNodes(): HierarchyNode[] {
    const context = {
      pageNumber: 1,
    };
    let rootBiobookChapters: BiobookChapter[];
    if (this.noEmptyRoots) {
      rootBiobookChapters =
        this.biobookChapters.filter((biobookChapter) => {
          const parent = this.getImmediateNonEmptyAscendant(biobookChapter);
          return (
            biobookChapter.phraseCount > 0 &&
            (parent == null || (parent.phraseCount === 0 && parent.parentLabelId == null))
          );
        }) ?? [];
    } else {
      rootBiobookChapters = this.biobookChapters.filter((biobookChapter) => biobookChapter.parentLabelId == null) ?? [];
    }
    return rootBiobookChapters
      .sort((a, b) => a.showOrder - b.showOrder)
      .map((biobookChapter, index) => this.buildNode(biobookChapter, index, [], context));
  }
  private buildNode(
    biobookChapter: BiobookChapter,
    index: number,
    path: number[],
    context: {
      pageNumber: number;
    },
  ): HierarchyNode {
    const pageNumber = context.pageNumber;
    context.pageNumber += biobookChapter.phraseCount;
    const filteredBiobookChapter = this.biobookChapters.find((item) => item.labelId === biobookChapter.labelId) ?? null;
    return {
      children:
        this.getImmediateNonEmptyDescendents(biobookChapter)
          .sort((a, b) => (a.showOrder ?? Number.MAX_SAFE_INTEGER) - (b.showOrder ?? Number.MAX_SAFE_INTEGER))
          .map((l, i) => this.buildNode(l, i, [...path, index + 1], context)) ?? [],
      data: {
        biobookChapter,
        filteredBiobookChapter,
        pageNumber,
        path: [...path, index + 1],
        descendentPhraseCount: this.getNonEmptyDescendents(biobookChapter).reduce(
          (acc, {phraseCount}) => acc + phraseCount,
          0,
        ),
      },
      label: biobookChapter.labelName,
      key: `${biobookChapter.labelId}`,
    };
  }
  private getParent(biobookChapter: BiobookChapter): BiobookChapter | null {
    return this.biobookChapters.find((item) => item.labelId === biobookChapter.parentLabelId) ?? null;
  }
  private getImmediateNonEmptyAscendant(biobookChapter: BiobookChapter): BiobookChapter | null {
    let parent = this.getParent(biobookChapter);
    while (parent != null && parent.parentLabelId != null && parent.phraseCount === 0) {
      parent = this.getParent(parent);
    }
    return parent;
  }
  private getImmediateNonEmptyDescendents(biobookChapter: BiobookChapter): BiobookChapter[] {
    return (
      this.biobookChapters.filter(
        (item) => item.phraseCount > 0 && this.getImmediateNonEmptyAscendant(item) === biobookChapter,
      ) ?? []
    );
  }
  private getNonEmptyDescendents(biobookChapter: BiobookChapter): BiobookChapter[] {
    const immediateNonEmptyDescendents = this.getImmediateNonEmptyDescendents(biobookChapter);
    return [
      biobookChapter,
      ...immediateNonEmptyDescendents.map((descendent) => this.getNonEmptyDescendents(descendent)),
    ].flat();
  }
}
